import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Typography,
  Checkbox,
  notification,
} from "lib/ui";
import {
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
} from "lib/icons";
import {
  Users,
  Truck,
  User,
  MapPin,
  Eye,
  Pencil,
  Trash2,
  Radio,
} from "lucide-react";
import moment from "moment";
import { AuthContext } from "../../../context/Context";
import ApiPath from "../../../hooks/ApiFetch";
import {
  PeopleDataTable,
  ActionBtn,
  PersonCell,
  TypeBadge,
  BoolPill,
} from "../../../components/admin/PeopleDataTable";
import DeliveryAddForm from "../DeliveryProfile/Add";

const { Option } = Select;
const { Text } = Typography;

const TABS = [
  { id: "all", label: "All people", icon: Users },
  { id: "users", label: "Platform users", icon: User },
  { id: "delivery", label: "Delivery & drivers", icon: Truck },
];

function displayName(user) {
  if (user?.name?.trim()) return user.name.trim();
  const parts = [user?.firstname, user?.lastname].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

function userType(user) {
  if (user?.position === "team" || user?.roles?.includes("team")) return "team";
  if (user?.position === "driver" || user?.roles?.includes("driver")) return "driver";
  return "user";
}

const PeopleHub = () => {
  const { Users: users, setUser: setUsers } = useContext(AuthContext);
  const navigate = useNavigate();

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [deliveryData, setDeliveryData] = useState([]);
  const [loadingDelivery, setLoadingDelivery] = useState(true);

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm] = Form.useForm();
  const [userSaving, setUserSaving] = useState(false);

  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [deliveryForm] = Form.useForm();

  const fetchUsers = useCallback(async () => {
    try {
      const res = await ApiPath.get("/users");
      setUsers(res.data);
    } catch {
      message.error("Failed to load users");
    }
  }, [setUsers]);

  const fetchDelivery = useCallback(async () => {
    setLoadingDelivery(true);
    try {
      const res = await ApiPath.get("/deliveryprofile");
      setDeliveryData(Array.isArray(res.data) ? res.data : []);
    } catch {
      message.error("Failed to load delivery profiles");
    } finally {
      setLoadingDelivery(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchDelivery();
  }, [fetchUsers, fetchDelivery]);

  const userList = Array.isArray(users) ? users : [];
  const q = search.trim().toLowerCase();

  const filteredUsers = useMemo(() => {
    return userList.filter((item) => {
      if (!q) return true;
      const name = displayName(item).toLowerCase();
      return (
        name.includes(q) ||
        (item.email || "").toLowerCase().includes(q) ||
        (item.mobileNumber || "").toLowerCase().includes(q) ||
        (item.position || "").toLowerCase().includes(q) ||
        (item.roles || []).join(" ").toLowerCase().includes(q)
      );
    });
  }, [userList, q]);

  const filteredDelivery = useMemo(() => {
    return deliveryData.filter((item) => {
      if (!q) return true;
      return (
        (item.email || "").toLowerCase().includes(q) ||
        (item.mobile_number || "").toLowerCase().includes(q)
      );
    });
  }, [deliveryData, q]);

  const stats = useMemo(
    () => ({
      users: userList.length,
      delivery: deliveryData.length,
      drivers: userList.filter(
        (u) =>
          u.position === "driver" || (u.roles || []).includes("driver")
      ).length,
      online: deliveryData.filter((d) => d.is_online).length,
    }),
    [userList, deliveryData]
  );

  const unifiedRows = useMemo(() => {
    const u = filteredUsers.map((row) => ({
      ...row,
      _rowKind: "user",
      _sortName: displayName(row),
    }));
    const d = filteredDelivery.map((row) => ({
      ...row,
      _rowKind: "delivery",
      _sortName: row.email || "",
    }));
    return [...u, ...d].sort((a, b) =>
      (a._sortName || "").localeCompare(b._sortName || "", undefined, {
        sensitivity: "base",
      })
    );
  }, [filteredUsers, filteredDelivery]);

  const activeRows =
    tab === "users"
      ? filteredUsers
      : tab === "delivery"
        ? filteredDelivery
        : unifiedRows;

  /* —— User CRUD —— */
  const openCreateUser = () => {
    userForm.resetFields();
    setEditingUser(null);
    setUserModalOpen(true);
  };

  const openEditUser = (record) => {
    userForm.setFieldsValue({
      ...record,
      createdAt: record.createdAt ? moment(record.createdAt) : undefined,
      updatedAt: record.updatedAt ? moment(record.updatedAt) : undefined,
    });
    setEditingUser(record);
    setUserModalOpen(true);
  };

  const deleteUser = async (id) => {
    try {
      await ApiPath.delete(`/users/${id}`);
      setUsers(userList.filter((item) => item._id !== id));
      message.success("User deleted");
    } catch {
      message.error("Failed to delete user");
    }
  };

  const submitUser = async () => {
    try {
      const values = await userForm.validateFields();
      setUserSaving(true);
      if (editingUser) {
        await ApiPath.put(`/users/${editingUser._id}`, values);
        setUsers(
          userList.map((item) =>
            item._id === editingUser._id ? { ...item, ...values } : item
          )
        );
        message.success("User updated");
      } else {
        const res = await ApiPath.post("/users", values);
        setUsers([...userList, res.data]);
        message.success("User created");
      }
      setUserModalOpen(false);
      userForm.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.message || "Operation failed");
    } finally {
      setUserSaving(false);
    }
  };

  /* —— Delivery CRUD —— */
  const openCreateDelivery = () => {
    deliveryForm.resetFields();
    setEditingDelivery(null);
    setDeliveryModalOpen(true);
  };

  const openEditDelivery = (record) => {
    deliveryForm.setFieldsValue(record);
    setEditingDelivery(record);
    setDeliveryModalOpen(true);
  };

  const deleteDelivery = (id, title) => {
    Modal.confirm({
      title: "Delete delivery profile?",
      icon: <DeleteOutlined />,
      content: title ? `Remove ${title}` : undefined,
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await ApiPath.delete(`/deliveryprofile/${id}`);
          setDeliveryData((prev) => prev.filter((item) => item._id !== id));
          notification.success({
            message: "Deleted",
            description: "Delivery profile removed.",
          });
        } catch {
          notification.error({
            message: "Failed",
            description: "Could not delete profile.",
          });
        }
      },
    });
  };

  const submitDelivery = async (values) => {
    try {
      if (editingDelivery) {
        await ApiPath.put(`/deliveryprofile/${editingDelivery._id}`, values);
        setDeliveryData((prev) =>
          prev.map((item) =>
            item._id === editingDelivery._id ? { ...item, ...values } : item
          )
        );
        notification.success({
          message: "Updated",
          description: "Delivery profile saved.",
        });
      } else {
        const res = await ApiPath.post("/deliveryprofile", values);
        setDeliveryData((prev) => [
          ...prev,
          { ...values, _id: res.data._id ?? res.data?._id },
        ]);
        notification.success({
          message: "Added",
          description: "Delivery profile created.",
        });
      }
      setDeliveryModalOpen(false);
      deliveryForm.resetFields();
    } catch {
      notification.error({
        message: "Failed",
        description: "Could not save delivery profile.",
      });
    }
  };

  const userColumns = [
    {
      key: "person",
      header: "Person",
      render: (row) => (
        <PersonCell name={displayName(row)} email={row.email} />
      ),
    },
    {
      key: "mobile",
      header: "Mobile",
      dataIndex: "mobileNumber",
      render: (row) => (
        <span className="font-mono text-sm text-slate-700">
          {row.mobileNumber || "—"}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (row) => <TypeBadge type={userType(row)} />,
    },
    {
      key: "roles",
      header: "Roles",
      render: (row) => (
        <div className="flex max-w-[180px] flex-wrap gap-1">
          {(row.roles || []).length ? (
            row.roles.map((r) => (
              <span
                key={r}
                className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700"
              >
                {r}
              </span>
            ))
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </div>
      ),
    },
    {
      key: "position",
      header: "Position",
      render: (row) => (
        <span className="text-sm capitalize text-slate-600">
          {row.position || "—"}
        </span>
      ),
    },
    {
      key: "verified",
      header: "Verified",
      render: (row) => (
        <BoolPill
          value={row.mobileVerified}
          trueLabel="Verified"
          falseLabel="Pending"
        />
      ),
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      render: (row) => (
        <span className="tabular-nums font-semibold text-slate-900">
          {Number(row.userBalance ?? 0).toFixed(2)}{" "}
          <span className="font-normal text-slate-400">AED</span>
        </span>
      ),
    },
    {
      key: "created",
      header: "Joined",
      render: (row) => (
        <span className="text-xs text-slate-500">
          {row.createdAt
            ? moment(row.createdAt).format("DD MMM YYYY")
            : "—"}
        </span>
      ),
    },
  ];

  const deliveryColumns = [
    {
      key: "person",
      header: "Account",
      render: (row) => (
        <PersonCell
          name={row.email?.split("@")[0] || "Driver"}
          email={row.email}
          subtitle={row.mobile_number}
        />
      ),
    },
    {
      key: "mobile",
      header: "Mobile",
      render: (row) => (
        <span className="font-mono text-sm">{row.mobile_number || "—"}</span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: () => <TypeBadge type="delivery" />,
    },
    {
      key: "assigned",
      header: "Assigned",
      render: (row) => <BoolPill value={row.assigned} />,
    },
    {
      key: "online",
      header: "Online",
      render: (row) => (
        <BoolPill
          value={row.is_online}
          trueLabel="Online"
          falseLabel="Offline"
        />
      ),
    },
    {
      key: "verified",
      header: "Verified",
      render: (row) => <BoolPill value={row.is_verified} />,
    },
    {
      key: "location",
      header: "Location",
      render: (row) => (
        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
          <MapPin size={14} className="shrink-0 text-brand" />
          {row.latitude != null && row.longitude != null
            ? `${Number(row.latitude).toFixed(4)}, ${Number(row.longitude).toFixed(4)}`
            : "—"}
        </span>
      ),
    },
    {
      key: "created",
      header: "Added",
      render: (row) => (
        <span className="text-xs text-slate-500">
          {row.createdAt
            ? moment(row.createdAt).format("DD MMM YYYY")
            : "—"}
        </span>
      ),
    },
  ];

  const allColumns = [
    {
      key: "person",
      header: "Person / account",
      render: (row) =>
        row._rowKind === "user" ? (
          <PersonCell name={displayName(row)} email={row.email} />
        ) : (
          <PersonCell
            name={row.email?.split("@")[0] || "Delivery"}
            email={row.email}
          />
        ),
    },
    {
      key: "contact",
      header: "Contact",
      render: (row) => (
        <span className="font-mono text-sm text-slate-700">
          {row._rowKind === "user"
            ? row.mobileNumber || "—"
            : row.mobile_number || "—"}
        </span>
      ),
    },
    {
      key: "type",
      header: "Record type",
      render: (row) =>
        row._rowKind === "user" ? (
          <TypeBadge type={userType(row)} />
        ) : (
          <TypeBadge type="delivery" />
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) =>
        row._rowKind === "user" ? (
          <BoolPill
            value={row.mobileVerified}
            trueLabel="Verified"
            falseLabel="Pending"
          />
        ) : (
          <div className="flex flex-wrap gap-1">
            <BoolPill
              value={row.is_online}
              trueLabel="Online"
              falseLabel="Offline"
            />
            {row.assigned ? (
              <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">
                Assigned
              </span>
            ) : null}
          </div>
        ),
    },
    {
      key: "extra",
      header: "Details",
      render: (row) =>
        row._rowKind === "user" ? (
          <span className="text-xs text-slate-600">
            {(row.roles || []).join(", ") || row.position || "—"}
          </span>
        ) : (
          <span className="text-xs text-slate-600">
            {row.latitude != null
              ? `${Number(row.latitude).toFixed(3)}, ${Number(row.longitude).toFixed(3)}`
              : "—"}
          </span>
        ),
    },
    {
      key: "balance",
      header: "Balance / meta",
      align: "right",
      render: (row) =>
        row._rowKind === "user" ? (
          <span className="tabular-nums font-semibold">
            {Number(row.userBalance ?? 0).toFixed(2)} AED
          </span>
        ) : (
          <BoolPill value={row.is_verified} trueLabel="Verified" falseLabel="No" />
        ),
    },
  ];

  const renderUserActions = (row) => (
    <>
      <ActionBtn
        variant="view"
        title="View profile"
        onClick={() => navigate(`/users/user/${row._id}`)}
      >
        <Eye size={14} />
        View
      </ActionBtn>
      <ActionBtn variant="edit" title="Edit" onClick={() => openEditUser(row)}>
        <Pencil size={14} />
        Edit
      </ActionBtn>
      <ActionBtn
        variant="delete"
        title="Delete"
        onClick={() =>
          Modal.confirm({
            title: "Delete this user?",
            content: "This cannot be undone.",
            okText: "Delete",
            cancelText: "Cancel",
            okType: "danger",
            onOk: () => deleteUser(row._id),
          })
        }
      >
        <Trash2 size={14} />
      </ActionBtn>
    </>
  );

  const renderDeliveryActions = (row) => (
    <>
      <ActionBtn variant="edit" title="Edit" onClick={() => openEditDelivery(row)}>
        <Pencil size={14} />
        Edit
      </ActionBtn>
      <ActionBtn
        variant="delete"
        title="Delete"
        onClick={() => deleteDelivery(row._id, row.email)}
      >
        <Trash2 size={14} />
        Delete
      </ActionBtn>
    </>
  );

  const renderAllActions = (row) =>
    row._rowKind === "user"
      ? renderUserActions(row)
      : renderDeliveryActions(row);

  const tableColumns =
    tab === "users"
      ? userColumns
      : tab === "delivery"
        ? deliveryColumns
        : allColumns;

  const primaryAction =
    tab === "delivery" ? (
      <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDelivery}>
        Add delivery profile
      </Button>
    ) : tab === "users" ? (
      <Button type="primary" icon={<PlusOutlined />} onClick={openCreateUser}>
        Add platform user
      </Button>
    ) : (
      <div className="flex flex-wrap gap-2">
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateUser}>
          Add user
        </Button>
        <Button icon={<PlusOutlined />} onClick={openCreateDelivery}>
          Add delivery
        </Button>
      </div>
    );

  return (
    <div className="space-y-6 pb-10">
      <header className="relative overflow-hidden rounded-2xl border border-violet-200/60 bg-gradient-to-br from-brand-deeper via-brand-dark to-brand px-6 py-7 text-white shadow-[0_20px_50px_-12px_rgba(91,33,182,0.4)] md:px-8">
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-violet-100 ring-1 ring-white/15">
              <Users size={14} strokeWidth={2.25} aria-hidden />
              People & delivery
            </p>
            <h1 className="mt-3 text-2xl font-extrabold tracking-tight md:text-3xl">
              Customers, team & drivers
            </h1>
            <p className="mt-2 max-w-xl text-sm text-violet-100/90">
              Platform users and delivery profiles in one place — search, filter,
              and manage every account from a single workspace.
            </p>
          </div>
          {primaryAction}
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Platform users", value: stats.users, icon: Users },
          { label: "Delivery profiles", value: stats.delivery, icon: Truck },
          { label: "Drivers (users)", value: stats.drivers, icon: User },
          { label: "Online now", value: stats.online, icon: Radio },
        ].map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {label}
              </p>
              <span className="rounded-lg bg-brand-surface p-2 text-brand">
                <Icon size={18} />
              </span>
            </div>
            <p className="mt-2 text-2xl font-extrabold tabular-nums text-slate-900">
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card">
        <div
          className="grid grid-cols-1 gap-1.5 rounded-xl bg-slate-100 p-1.5 sm:grid-cols-3"
          role="tablist"
          aria-label="People filters"
        >
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition ${
                tab === id
                  ? "bg-white text-brand-dark shadow-sm ring-1 ring-slate-200/80"
                  : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
              }`}
            >
              <Icon size={16} strokeWidth={2.25} aria-hidden />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
        <Input
          placeholder="Search name, email, mobile, role…"
          prefix={<SearchOutlined />}
          className="w-full"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <p className="text-sm text-slate-500">
        Showing{" "}
        <span className="font-semibold text-slate-800">{activeRows.length}</span>{" "}
        records
        {tab === "all" && !loadingDelivery ? "" : loadingDelivery && tab !== "users" ? " · loading delivery…" : ""}
      </p>

      <PeopleDataTable
        columns={tableColumns}
        rows={activeRows}
        rowKey={(row) =>
          row._rowKind === "delivery"
            ? `delivery-${row._id}`
            : `user-${row._id}`
        }
        renderActions={
          tab === "users"
            ? renderUserActions
            : tab === "delivery"
              ? renderDeliveryActions
              : renderAllActions
        }
        emptyTitle="No matching records"
        emptyHint="Change the tab or clear your search."
      />

      <Modal
        title={editingUser ? "Edit platform user" : "Add platform user"}
        visible={userModalOpen}
        onOk={submitUser}
        onCancel={() => setUserModalOpen(false)}
        confirmLoading={userSaving}
        width={700}
        destroyOnClose
      >
        <Form form={userForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Name required" }]}
              >
                <Input placeholder="Full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Email required" },
                  { type: "email", message: "Invalid email" },
                ]}
              >
                <Input placeholder="Email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="mobileNumber"
                label="Mobile"
                rules={[{ required: true, message: "Mobile required" }]}
              >
                <Input placeholder="Mobile number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="roles"
                label="Roles"
                rules={[{ required: true, message: "Select roles" }]}
              >
                <Select mode="multiple" placeholder="Roles">
                  <Option value="admin">Admin</Option>
                  <Option value="user">User</Option>
                  <Option value="driver">Driver</Option>
                  <Option value="team">Team</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="position" label="Position">
                <Input placeholder="e.g. driver, team" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="userBalance" label="Balance (AED)">
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="mobileVerified"
            label="Verification"
            valuePropName="checked"
          >
            <Checkbox>Mobile verified</Checkbox>
          </Form.Item>
          {editingUser ? (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Created">
                  <Text>
                    {editingUser.createdAt
                      ? moment(editingUser.createdAt).format("LLL")
                      : "—"}
                  </Text>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Updated">
                  <Text>
                    {editingUser.updatedAt
                      ? moment(editingUser.updatedAt).format("LLL")
                      : "—"}
                  </Text>
                </Form.Item>
              </Col>
            </Row>
          ) : null}
        </Form>
      </Modal>

      <Modal
        title={editingDelivery ? "Edit delivery profile" : "Add delivery profile"}
        visible={deliveryModalOpen}
        onCancel={() => setDeliveryModalOpen(false)}
        footer={null}
        width={800}
      >
        <DeliveryAddForm
          form={deliveryForm}
          onFinish={submitDelivery}
          initialValues={editingDelivery || {}}
        />
      </Modal>
    </div>
  );
};

export default PeopleHub;
